import { useParams } from 'react-router-dom'
import {
  User as UserIcon,
  Calendar,
  Award,
  Building2,
  Crown,
  MessageSquare,
  Briefcase,
} from 'lucide-react'
import { useUser, useMyEmployments, useUserEmployments } from '@/hooks/useUser'
import { useAuthStore } from '@/stores/authStore'
import { formatDate, gravatarUrl } from '@/lib/utils'
import type { User, EmploymentAgreement } from '@/types/user'

/**
 * User Profile Page - displays user information and employments.
 *
 * Features:
 * - User info card with avatar, name, registration date
 * - Achievement progress
 * - Employment agreements (CEO positions)
 */

export function ProfilePage() {
  const { username: urlUsername } = useParams()
  const { username: currentUsername } = useAuthStore()

  // Use URL username if provided, otherwise show current user
  const targetUsername = urlUsername || currentUsername

  const { data: user, isLoading: userLoading, error: userError } = useUser(targetUsername || null)

  // Determine which employments hook to use
  const isOwnProfile = targetUsername === currentUsername
  const { data: myEmployments, isLoading: myEmpLoading } = useMyEmployments()
  const { data: userEmployments, isLoading: userEmpLoading } = useUserEmployments(
    !isOwnProfile && user?.id ? user.id : null
  )

  const employments = isOwnProfile ? myEmployments : userEmployments
  const employmentsLoading = isOwnProfile ? myEmpLoading : userEmpLoading

  // Loading state
  if (userLoading) {
    return (
      <div className="flex h-full items-center justify-center">
        <div className="text-center">
          <div className="mb-4 h-8 w-8 animate-spin rounded-full border-2 border-primary border-t-transparent" />
          <p className="text-text-light">Loading profile...</p>
        </div>
      </div>
    )
  }

  // Error state
  if (userError || !user) {
    return (
      <div className="flex h-full flex-col items-center justify-center p-8 text-center">
        <UserIcon className="mb-4 h-16 w-16 text-text-muted" />
        <p className="mb-2 text-lg font-semibold text-text-dark">User not found</p>
        <p className="text-text-light">The user "{targetUsername}" could not be found.</p>
      </div>
    )
  }

  return (
    <div className="flex flex-col overflow-hidden">
      {/* Header */}
      <div className="border-b border-gray-200 bg-white px-4 py-4 sm:px-6">
        <h1 className="text-2xl font-semibold text-text-dark">
          {isOwnProfile ? 'My Profile' : 'User Profile'}
        </h1>
      </div>

      <div className="flex-1 overflow-y-auto p-4 sm:p-6">
        <div className="mx-auto max-w-4xl space-y-6">
          {/* User Info Card */}
          <UserInfoCard user={user} isOwnProfile={isOwnProfile} />

          {/* Employments Section */}
          <div className="rounded-lg border border-gray-200 bg-white">
            <div className="border-b border-gray-200 px-4 py-3">
              <h2 className="flex items-center gap-2 font-semibold text-text-dark">
                <Briefcase className="h-5 w-5 text-primary" />
                CEO Positions
                {employments && <span className="text-text-muted">({employments.length})</span>}
              </h2>
            </div>

            <div className="p-4">
              {employmentsLoading ? (
                <div className="flex items-center justify-center py-8">
                  <div className="h-6 w-6 animate-spin rounded-full border-2 border-primary border-t-transparent" />
                </div>
              ) : !employments || employments.length === 0 ? (
                <div className="py-8 text-center text-text-muted">
                  <Building2 className="mx-auto mb-2 h-12 w-12 opacity-50" />
                  <p>No CEO positions</p>
                </div>
              ) : (
                <div className="space-y-3">
                  {employments.map((employment) => (
                    <EmploymentCard key={employment.id} employment={employment} />
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}

// User Info Card Component
interface UserInfoCardProps {
  user: User
  isOwnProfile: boolean
}

function UserInfoCard({ user, isOwnProfile }: UserInfoCardProps) {
  const { username, gravatarHash, registrationDate, userCapabilities } = user

  // Safely access userCapabilities with defaults
  const achievementCount = userCapabilities?.achievementCount ?? 0
  const achievementTotal = userCapabilities?.achievementTotal ?? 0
  const isPremium = userCapabilities?.premium ?? false

  const achievementPercent =
    achievementTotal > 0 ? Math.round((achievementCount / achievementTotal) * 100) : 0

  return (
    <div className="rounded-lg border border-gray-200 bg-white p-6">
      <div className="flex flex-col items-center gap-6 sm:flex-row sm:items-start">
        {/* Avatar */}
        <img
          src={gravatarUrl(gravatarHash, 128)}
          alt={username}
          className="h-32 w-32 rounded-full border-4 border-primary-100"
        />

        {/* Info */}
        <div className="flex-1 text-center sm:text-left">
          <div className="mb-4">
            <h2 className="text-2xl font-bold text-text-dark">{username}</h2>
            <div className="mt-1 flex flex-wrap items-center justify-center gap-3 text-sm text-text-light sm:justify-start">
              {registrationDate && (
                <span className="flex items-center gap-1">
                  <Calendar className="h-4 w-4" />
                  Joined {formatDate(registrationDate)}
                </span>
              )}
              {isPremium && (
                <span className="flex items-center gap-1 text-yellow-600">
                  <Crown className="h-4 w-4" />
                  Premium
                </span>
              )}
            </div>
          </div>

          {/* Achievements */}
          <div className="mb-4">
            <div className="mb-1 flex items-center justify-center gap-2 text-sm sm:justify-start">
              <Award className="h-4 w-4 text-primary" />
              <span>
                Achievements: {achievementCount} / {achievementTotal}
              </span>
            </div>
            <div className="mx-auto h-2 w-full max-w-xs overflow-hidden rounded-full bg-gray-200 sm:mx-0">
              <div
                className="h-full bg-primary transition-all"
                style={{ width: `${achievementPercent}%` }}
              />
            </div>
          </div>

          {/* Actions */}
          {!isOwnProfile && (
            <div className="flex justify-center gap-2 sm:justify-start">
              <button className="flex items-center gap-2 rounded-md bg-primary px-4 py-2 text-sm font-medium text-white hover:bg-primary-dark">
                <MessageSquare className="h-4 w-4" />
                Send Message
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  )
}

// Employment Card Component
interface EmploymentCardProps {
  employment: EmploymentAgreement
}

function EmploymentCard({ employment }: EmploymentCardProps) {
  const { company, startDate } = employment

  // Guard against missing company data
  if (!company) {
    return null
  }

  return (
    <div className="flex items-center gap-4 rounded-lg border border-gray-100 bg-gray-50 p-4">
      {/* Company Logo */}
      <div className="flex h-12 w-12 items-center justify-center rounded-lg bg-white">
        {company.logoUrl ? (
          <img
            src={company.logoUrl}
            alt={company.name || 'Company'}
            className="h-10 w-10 object-contain"
          />
        ) : (
          <Building2 className="h-6 w-6 text-text-muted" />
        )}
      </div>

      {/* Company Info */}
      <div className="flex-1">
        <a
          href={`/security/${company.securityIdentifier || ''}`}
          className="font-medium text-text-dark hover:text-primary"
        >
          {company.name || 'Unknown Company'}
        </a>
        <p className="text-xs text-text-muted">{company.securityIdentifier || '-'}</p>
      </div>

      {/* Employment Details */}
      <div className="text-right text-sm">
        <p className="text-text-muted">CEO since</p>
        <p className="font-medium">{startDate ? formatDate(startDate) : '-'}</p>
      </div>
    </div>
  )
}
